import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { getAdminOrganizationPermissions, getAdminOrganizationSubscription, getAdminOrganizationUsage } from "../../../shared/requests";
import { AdminOrganizationContextDetail } from "../../types/adminEntities";

type AdminOrganizationContextCacheValue = {
  clearOrganizationContext: (organizationId: string) => void;
  clearAllOrganizationContexts: () => void;
  contextsByOrganizationId: Record<string, AdminOrganizationContextDetail>;
  ensureOrganizationContext: (organizationId: string, options?: { force?: boolean; }) => Promise<AdminOrganizationContextDetail | null>;
  setOrganizationContext: (organizationId: string, context: AdminOrganizationContextDetail) => void;
};

const AdminOrganizationContextCacheContext = createContext<AdminOrganizationContextCacheValue | null>(null);

type AdminOrganizationContextCacheProviderProps = {
  children: ReactNode;
};

export const AdminOrganizationContextCacheProvider = ({ children }: AdminOrganizationContextCacheProviderProps) => {
  const [contextsByOrganizationId, setContextsByOrganizationId] = useState<Record<string, AdminOrganizationContextDetail>>({});
  const inflightRequestsRef = useRef<Record<string, Promise<AdminOrganizationContextDetail | null>>>({});

  const setOrganizationContext = useCallback((organizationId: string, context: AdminOrganizationContextDetail) => {
    setContextsByOrganizationId((current) => ({ ...current, [organizationId]: context }));
  }, []);

  const clearOrganizationContext = useCallback((organizationId: string) => {
    setContextsByOrganizationId((current) => {
      if (!(organizationId in current)) {
        return current;
      }
      const next = { ...current };
      delete next[organizationId];
      return next;
    });
    delete inflightRequestsRef.current[organizationId];
  }, []);

  const clearAllOrganizationContexts = useCallback(() => {
    setContextsByOrganizationId({});
    inflightRequestsRef.current = {};
  }, []);

  const ensureOrganizationContext = useCallback(async (organizationId: string, options?: { force?: boolean; }) => {
    const normalizedOrganizationId = organizationId.trim();
    if (normalizedOrganizationId.length === 0) {
      return null;
    }

    if (!options?.force && contextsByOrganizationId[normalizedOrganizationId]) {
      return contextsByOrganizationId[normalizedOrganizationId];
    }

    const existingRequest = inflightRequestsRef.current[normalizedOrganizationId];
    if (existingRequest) {
      return existingRequest;
    }

    const request = Promise.all([
      getAdminOrganizationPermissions({ organizationId: normalizedOrganizationId }),
      getAdminOrganizationSubscription({ organizationId: normalizedOrganizationId }),
      getAdminOrganizationUsage({ organizationId: normalizedOrganizationId }),
    ])
      .then(([permissionsResponse, subscriptionResponse, usageResponse]) => {
        const payload = {
          organizationPermissions: permissionsResponse.payload,
          organizationUsage: usageResponse.payload,
          activeSubscription: subscriptionResponse.payload,
        };
        setContextsByOrganizationId((current) => ({ ...current, [normalizedOrganizationId]: payload }));
        return payload;
      })
      .catch(() => null)
      .finally(() => {
        delete inflightRequestsRef.current[normalizedOrganizationId];
      });

    inflightRequestsRef.current[normalizedOrganizationId] = request;
    return request;
  }, [contextsByOrganizationId]);

  const value = useMemo<AdminOrganizationContextCacheValue>(() => ({
    clearOrganizationContext,
    clearAllOrganizationContexts,
    contextsByOrganizationId,
    ensureOrganizationContext,
    setOrganizationContext,
  }), [clearOrganizationContext, clearAllOrganizationContexts, contextsByOrganizationId, ensureOrganizationContext, setOrganizationContext]);

  return (
    <AdminOrganizationContextCacheContext.Provider value={value}>
      {children}
    </AdminOrganizationContextCacheContext.Provider>
  );
};

export const useAdminOrganizationContextCache = () => {
  const value = useContext(AdminOrganizationContextCacheContext);
  if (!value) {
    throw new Error("AdminOrganizationContextCacheProvider is missing.");
  }
  return value;
};
